/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Alert, Box, Stack, Typography } from "@mui/material";
import { PanelErrorBoundary } from "components/common/error/PanelErrorBoundary";
import {
  HotkeyDescription,
  HotkeyExplanation,
} from "components/identifyEventsTool/tool/HotkeyExplanation";
import { MassDataPanel } from "components/identifyEventsTool/tool/MassDataPanel";
import { ScrapEyesEventsPanel } from "components/identifyEventsTool/tool/ScrapEyesEventsPanel";
import { MultipleAnglesVideoPlayer } from "components/scrapEyeDataDashboard/VideoPlayer/components/MultipleAnglesVideoPlayer";
import {
  PLAYBACK_SCOPE,
  PLAYBACK_SPEEDS,
} from "components/scrapEyeDataDashboard/VideoPlayer/constants";
import {
  EVENT_LABELLING_DELETE_SCOPE,
  EVENT_LABELLING_SCOPE,
  IDENTIFY_EVENTS_HOTKEY_SCOPES,
} from "components/manualReview/hotkeyScopes";
import {
  VideoPlayerGroupProvider,
  useVideoPlayerGroupContext,
} from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoPlayerGroupContext";
import {
  HotkeyProvider,
  useHotkeyContext,
} from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { TimelineZoomProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/TimelineZoomContext";
import { VideoControlProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { VideoSuspensionProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoSuspensionContext";
import { LabellingOverlays } from "components/scrapEyeLabelling/flows/LabellingOverlays";
import { useAreaMaterialMappingUi } from "components/scrapEyeLabelling/hooks/useAreaMaterialMappingUi";
import { useAreaMaterialState } from "components/scrapEyeLabelling/hooks/useAreaMaterialState";
import { useLabellingKeyboard } from "components/scrapEyeLabelling/hooks/useLabellingKeyboard";
import { usePendingAreaSelection } from "components/scrapEyeLabelling/hooks/usePendingAreaSelection";
import { AreaMaterialMappingModal } from "components/scrapEyeLabelling/modals/AreaMaterialMappingModal";
import {
  useAreaMaterialColors,
  useAreaMaterialLabels,
  useReportedRailcarMaterials,
} from "components/scrapEyeLabelling/hooks/useReportedRailcarMaterials";
import { AreaSelectionModal } from "components/scrapEyeLabelling/modals/AreaSelectionModal";
import { AreaMaterialSnapshot } from "components/scrapEyeLabelling/types";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenant } from "hooks/settings";
import { useActivityTimer } from "hooks/useActivityTimer";
import { useMaterials } from "hooks/useMaterials";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHotkeys } from "react-hotkeys-hook";
import { useNavigate, useParams } from "react-router";
import {
  EventCreate,
  LabellingReservation,
  RailcarSlotMaterialSpan,
  useGetReservedPeriodsQuery,
} from "src/store/api/generatedApi";
import { LabellingGuideButton } from "../guide/LabellingGuideButton";
import { ActivityTimer } from "components/common/ActivityTimer";
import { useEventManagement } from "../hooks/useEventManagement";
import { ActiveChargeScheduleMaterials } from "./ActiveChargeScheduleMaterials";
import { RailcarMovements } from "./RailcarMovements";
import { RailcarTimeline } from "./RailcarTimeline";
import { useTenantData } from "contexts/tenant";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { CopyLinkButton } from "components/labelling/CopyLinkButton";
import { z } from "zod";

dayjs.extend(utc);

export const IdentifyEventsTool = () => {
  const { location } = useParams<{ location: string }>();
  const tenantName = useTenant();
  const { data: reservedPeriods, isFetching: isReservedPeriodsFetching } =
    useGetReservedPeriodsQuery({
      tenantName,
      location: location ?? "",
    });
  const firstPeriod = useMemo(() => {
    if (!reservedPeriods?.reservations) return null;
    return reservedPeriods.reservations[0];
  }, [reservedPeriods]);
  const lastPeriod = useMemo(() => {
    if (!reservedPeriods?.reservations) return null;
    return (
      reservedPeriods.reservations[reservedPeriods.reservations.length - 1] ??
      null
    );
  }, [reservedPeriods]);

  const navigate = useNavigate();

  useEffect(() => {
    if (reservedPeriods?.reservations?.length === 0) {
      void navigate(`/${tenantName}/tool/identify-events`);
    }
  }, [reservedPeriods, navigate, tenantName]);

  const candidateEvents = useMemo(
    () => reservedPeriods?.candidate_events ?? [],
    [reservedPeriods?.candidate_events]
  );

  if (!firstPeriod || !lastPeriod || !location) {
    return null;
  }
  return (
    <HotkeyProvider>
      <VideoControlProvider>
        <VideoPlayerGroupProvider location={location}>
          <ScrapEyesLabellingToolInner
            location={location}
            firstPeriod={firstPeriod}
            lastPeriod={lastPeriod}
            candidateEvents={candidateEvents}
            railcarSlotMaterials={
              isReservedPeriodsFetching
                ? undefined
                : reservedPeriods?.railcar_slot_materials
            }
            isLoadingRailcarSlotMaterials={isReservedPeriodsFetching}
          />
        </VideoPlayerGroupProvider>
      </VideoControlProvider>
    </HotkeyProvider>
  );
};

const ScrapEyesLabellingToolInner = ({
  location,
  firstPeriod,
  lastPeriod,
  candidateEvents: initialCandidateEvents,
  railcarSlotMaterials,
  isLoadingRailcarSlotMaterials,
}: {
  location: string;
  firstPeriod: LabellingReservation;
  lastPeriod: LabellingReservation;
  candidateEvents: EventCreate[];
  railcarSlotMaterials: RailcarSlotMaterialSpan[] | undefined;
  isLoadingRailcarSlotMaterials: boolean;
}) => {
  const { getHotkey } = useHotkeyContext();
  const { deploy_process: deployProcess } = useTenantData();
  const { t } = useTranslation();
  const tenantName = useTenant();
  const eventHotkeys: HotkeyDescription[] = useMemo(
    () => [
      { hotkeyId: "snapToCandidate", description: t("snapToCandidate") },
      { hotkeyId: "submitEvents", description: t("submitEvents") },
    ],
    [t]
  );
  const {
    elapsedSeconds,
    isActive,
    getElapsedSeconds,
    reset: resetTimer,
  } = useActivityTimer({
    storageKey: location
      ? `labelling-timer-${tenantName}-${location}`
      : undefined,
  });

  const videoStart = useMemo(() => dayjs.utc(firstPeriod.start), [firstPeriod]);
  const videoEnd = useMemo(() => dayjs.utc(lastPeriod.end), [lastPeriod]);
  const fullDuration = useMemo(
    () => videoEnd.diff(videoStart, "second"),
    [videoStart, videoEnd]
  );

  const { label_recording_playback_rate: tenantLabelRecordingPlaybackRate } =
    useScrapEyeMetadata();
  const [labelRecordingPlaybackRate, setLabelRecordingPlaybackRate] =
    useLocalStorageState(
      `identifyEvents.${tenantName}.labelRecordingPlaybackRate`,
      tenantLabelRecordingPlaybackRate,
      { schema: z.number().refine((rate) => PLAYBACK_SPEEDS.includes(rate)) }
    );

  const {
    events: sortedEvents,
    selectedEventId,
    recordingEventId,
    handleSelectEvent,
    handleDeleteEvent,
    handleStartRecording,
    handleStopRecording,
    handleSetMaterial,
    handleSetArea,
    handleUpdateEvent,
    handleReverseEventType,
    handleAddFlag,
    handleSnapToCandidate,
    areaMaterialSnapshots,
    getAreaMaterialMappingAtTime,
    updateAreaMaterialMapping,
    deleteAreaMaterialSnapshot,
    isUsingPersistentDefaults,
    commitPersistentMapping,
    defaultAreaMaterials,
  } = useEventManagement(
    `scrap-eyes-labelling-events-${firstPeriod.labelling_period_id}-${lastPeriod.labelling_period_id}`,
    videoStart,
    useCallback((event) => event, []),
    "shared",
    initialCandidateEvents,
    location,
    videoEnd,
    labelRecordingPlaybackRate
  );

  const { materials } = useMaterials();
  const filteredMaterials = useMemo(() => {
    return (
      materials?.filter((material) => material.labelling_name !== null) ?? []
    );
  }, [materials]);

  const { cycleAllAngles, resetAllToAuto } = useVideoPlayerGroupContext();

  const {
    pendingAreaSelection,
    setPendingAreaSelection,
    clearPendingAreaSelection,
  } = usePendingAreaSelection();

  const {
    isAreaMaterialMappingOpen,
    openAreaMaterialMappingModal,
    editingSnapshot,
    startEditingSnapshot,
    clearEditingSnapshot,
    closeAll: closeAreaMaterialModals,
  } = useAreaMaterialMappingUi();

  const {
    currentTimestamp,
    currentAreaMaterialMapping,
    handleAreaMaterialUpdate,
    handleSnapshotMappingUpdate,
    editingSnapshotCurrentMapping,
  } = useAreaMaterialState({
    videoStart,
    getAreaMaterialMappingAtTime,
    updateAreaMaterialMapping,
    deleteAreaMaterialSnapshot,
    editingSnapshot,
  });

  const reportedRailcarMaterials = useReportedRailcarMaterials(
    railcarSlotMaterials,
    currentTimestamp
  );

  const reportedMaterialsAtMappingTime = useReportedRailcarMaterials(
    railcarSlotMaterials,
    editingSnapshot?.timestamp ?? currentTimestamp
  );

  const currentAreaMaterialLabels = useAreaMaterialLabels(
    currentAreaMaterialMapping,
    reportedRailcarMaterials
  );

  const miniMapAreaColors = useAreaMaterialColors(
    currentAreaMaterialMapping,
    reportedRailcarMaterials
  );

  const {
    isAreaModeActive,
    isShiftHeld,
    binEntryActive,
    binBuffer,
    binEntryAvailable,
  } = useLabellingKeyboard({
    events: sortedEvents,
    selectedEventId,
    recordingEventId,
    videoStart,
    filteredMaterials,
    altAreaBehavior: "record",
    location,
    callbacks: {
      handleStartRecording,
      handleStopRecording,
      handleDeleteEvent,
      handleSetMaterial,
      handleSetArea,
      handleReverseEventType,
      handleAddFlag,
      handleSnapToCandidate,
      setPendingAreaSelection,
      openAreaMaterialMappingModal,
      cycleAllAngles,
      resetAllToAuto,
    },
  });

  const editLastEventKey = getHotkey("editLastEvent");
  useHotkeys(
    editLastEventKey,
    () => {
      const targetEvent = selectedEventId
        ? sortedEvents.find((e) => e.id === selectedEventId)
        : sortedEvents[sortedEvents.length - 1];

      if (targetEvent && targetEvent.eventType !== "flag") {
        setPendingAreaSelection({
          eventId: targetEvent.id,
          eventType: targetEvent.eventType,
          openedOnKeyRelease: false,
        });
      }
    },
    { keydown: true, preventDefault: true, scopes: [EVENT_LABELLING_SCOPE] },
    [editLastEventKey, sortedEvents, selectedEventId, setPendingAreaSelection]
  );

  const deleteEventKey = getHotkey("deleteEvent");
  useHotkeys(
    deleteEventKey,
    () => {
      const eventId =
        selectedEventId ?? sortedEvents[sortedEvents.length - 1]?.id;
      if (eventId) {
        handleDeleteEvent(eventId);
      }
    },
    {
      keydown: true,
      preventDefault: true,
      scopes: [EVENT_LABELLING_DELETE_SCOPE],
      enabled: !binEntryActive,
    },
    [
      deleteEventKey,
      sortedEvents,
      selectedEventId,
      handleDeleteEvent,
      binEntryActive,
    ]
  );

  const handleDeleteSnapshot = useCallback(
    (snapshot: AreaMaterialSnapshot) => {
      deleteAreaMaterialSnapshot(snapshot.timestamp);
      clearEditingSnapshot();
    },
    [deleteAreaMaterialSnapshot, clearEditingSnapshot]
  );

  const handleResetToDefaults = useCallback(() => {
    for (const area of Object.keys(currentAreaMaterialMapping)) {
      const defaultMaterial = defaultAreaMaterials[area] ?? "unknown";
      handleAreaMaterialUpdate(area, defaultMaterial);
    }
  }, [
    currentAreaMaterialMapping,
    defaultAreaMaterials,
    handleAreaMaterialUpdate,
  ]);

  const handleMappingSnapshotClick = useCallback(
    (snapshot: AreaMaterialSnapshot) => {
      startEditingSnapshot(snapshot);
    },
    [startEditingSnapshot]
  );

  const handleAreaSelect = useCallback(
    (area: string) => {
      if (pendingAreaSelection) {
        // For lift events, automatically assign material from area-material mapping if available
        const updates: { area: string; material?: string } = { area };
        if (pendingAreaSelection.eventType === "lift") {
          const materialFromArea = currentAreaMaterialMapping[area];
          if (materialFromArea && materialFromArea !== "unknown") {
            updates.material = materialFromArea;
          }
        }
        handleUpdateEvent(pendingAreaSelection.eventId, updates);
      }
    },
    [pendingAreaSelection, handleUpdateEvent, currentAreaMaterialMapping]
  );
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const headerActions = useMemo(
    () => (
      <>
        <ActivityTimer elapsedSeconds={elapsedSeconds} isActive={isActive} />
        <LabellingGuideButton
          open={isGuideOpen}
          onOpenChange={setIsGuideOpen}
        />
      </>
    ),
    [elapsedSeconds, isActive, isGuideOpen]
  );

  const isRailcarMovementsEnabled =
    useIsBehindFeatureFlag("rail_car_movements");

  return (
    <TimelineZoomProvider fullDuration={fullDuration} videoStart={videoStart}>
      <Stack
        sx={{
          position: "relative",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Stack
          p={1}
          gap={2}
          sx={{
            height: "100%",
            overflow: "hidden",
          }}
        >
          {isUsingPersistentDefaults ? (
            <Alert severity="info" sx={{ flexShrink: 0 }}>
              {t("areaMaterialMappingFromPreviousReservation")}
            </Alert>
          ) : null}
          <Stack
            direction="row"
            spacing={2}
            mt={1}
            sx={{ flex: 1, minHeight: 0 }}
          >
            <Stack
              spacing={2}
              direction="column"
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                minWidth: 0,
                maxWidth: "100%",
                overflowX: "hidden",
              }}
            >
              <PanelErrorBoundary panel="labelling-video">
                <VideoSuspensionProvider suspended={isGuideOpen}>
                  <MultipleAnglesVideoPlayer
                    hotkeyScope={PLAYBACK_SCOPE}
                    location={location}
                    startIso={firstPeriod.start}
                    endIso={lastPeriod.end}
                    events={sortedEvents}
                    selectedEventId={selectedEventId}
                    onEventClick={handleSelectEvent}
                    videoStart={videoStart}
                    areaMaterialSnapshots={areaMaterialSnapshots}
                    onMappingSnapshotClick={handleMappingSnapshotClick}
                    selectedSnapshotTimestamp={
                      editingSnapshot?.timestamp.unix() ?? null
                    }
                    actionButtons={
                      <CopyLinkButton
                        location={location}
                        videoStart={videoStart}
                      />
                    }
                  />
                </VideoSuspensionProvider>
              </PanelErrorBoundary>
              {isRailcarMovementsEnabled ? (
                <RailcarTimeline videoStart={videoStart} videoEnd={videoEnd} />
              ) : null}
              <PanelErrorBoundary panel="labelling-mass-chart">
                <MassDataPanel
                  startIso={firstPeriod.start}
                  endIso={lastPeriod.end}
                  videoStart={videoStart}
                  location={location}
                  events={sortedEvents}
                  candidateEvents={initialCandidateEvents}
                />
              </PanelErrorBoundary>
              <HotkeyExplanation
                eventHotkeys={eventHotkeys}
                areaColors={miniMapAreaColors}
                materials={filteredMaterials}
                location={location}
                labelRecordingPlaybackRate={labelRecordingPlaybackRate}
                onLabelRecordingPlaybackRateChange={
                  setLabelRecordingPlaybackRate
                }
                hotkeyConfigDisableScopes={IDENTIFY_EVENTS_HOTKEY_SCOPES}
              />
              <Typography variant="body2" whiteSpace="nowrap">
                {dayjs(firstPeriod.start).utc().format("DD/MM/YYYY HH:mm")} -{" "}
                {dayjs(lastPeriod.end).utc().format("DD/MM/YYYY HH:mm")} UTC
              </Typography>
            </Stack>
            <Stack
              spacing={1}
              sx={{
                width: 350,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                paddingBottom: 1,
                boxSizing: "border-box",
              }}
            >
              {isRailcarMovementsEnabled ? (
                <Box
                  sx={{
                    flexShrink: 1,
                    flexGrow: 0,
                    maxHeight: "50%",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <RailcarMovements startTime={videoStart} endTime={videoEnd} />
                </Box>
              ) : null}
              {["sync_and_sms", "edit_sync_and_sms"].includes(deployProcess) ? (
                <Box sx={{ flexShrink: 0, flexGrow: 0 }}>
                  <ActiveChargeScheduleMaterials
                    location={location}
                    videoStart={videoStart}
                    videoEnd={videoEnd}
                  />
                </Box>
              ) : null}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <ScrapEyesEventsPanel
                  events={sortedEvents}
                  selectedEventId={selectedEventId}
                  recordingEventId={recordingEventId}
                  onSelectEvent={handleSelectEvent}
                  onDeleteEvent={handleDeleteEvent}
                  onUpdateEvent={handleUpdateEvent}
                  materialOptions={filteredMaterials}
                  headerActions={headerActions}
                  location={location}
                  getAreaMaterialMappingAtTime={getAreaMaterialMappingAtTime}
                  onSubmitSuccess={commitPersistentMapping}
                  getElapsedSeconds={getElapsedSeconds}
                  resetTimer={resetTimer}
                />
              </Box>
            </Stack>
          </Stack>
          <Stack direction="column">
            <AreaSelectionModal
              open={pendingAreaSelection !== null}
              onClose={clearPendingAreaSelection}
              onSelectArea={handleAreaSelect}
              eventType={pendingAreaSelection?.eventType ?? "lift"}
              areaColors={miniMapAreaColors}
              areaMaterials={currentAreaMaterialLabels}
              onEditMaterials={openAreaMaterialMappingModal}
              openedOnKeyRelease={
                pendingAreaSelection?.openedOnKeyRelease ?? false
              }
              location={location}
            />

            <AreaMaterialMappingModal
              open={isAreaMaterialMappingOpen || editingSnapshot !== null}
              onClose={closeAreaMaterialModals}
              currentMapping={
                editingSnapshot
                  ? editingSnapshotCurrentMapping
                  : currentAreaMaterialMapping
              }
              reportedMaterials={reportedMaterialsAtMappingTime}
              isLoadingReportedMaterials={isLoadingRailcarSlotMaterials}
              onUpdateMapping={
                editingSnapshot
                  ? handleSnapshotMappingUpdate
                  : handleAreaMaterialUpdate
              }
              snapshot={editingSnapshot ?? undefined}
              onDeleteSnapshot={handleDeleteSnapshot}
              onResetToDefaults={handleResetToDefaults}
              location={location}
            />
          </Stack>
        </Stack>

        <LabellingOverlays
          isAreaModeActive={isAreaModeActive}
          isShiftHeld={isShiftHeld}
          binEntryActive={binEntryActive}
          binBuffer={binBuffer}
          binEntryAvailable={binEntryAvailable}
          areaColors={miniMapAreaColors}
          areaMaterials={currentAreaMaterialLabels}
          location={location}
        />
      </Stack>
    </TimelineZoomProvider>
  );
};
